/*
 * ICEKIVI hero — WebGL cursor-ripple + liquid image transitions.
 * Ported from the approved "New Drop Concept" artifact.
 * Falls back to a DOM crossfade slideshow when WebGL is unavailable,
 * and to a static image when prefers-reduced-motion is set.
 */
class IcekiviHero extends HTMLElement {
  connectedCallback() {
    this.rm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.timers = [];
    this.running = false;
    this.canvas = this.querySelector('[ref="rippleCanvas"]');
    this.panelsEl = this.querySelector('[ref="panels"]');
    if (!this.panelsEl) return;

    // Load-in reveal
    const markLoaded = () => this.classList.add('is-loaded');
    if (this.rm) markLoaded();
    else {
      const img = this.querySelector('[ref="panels"] .icekivi-hero__panel:nth-child(2) img');
      if (img && img.complete) setTimeout(markLoaded, 80);
      else if (img) {
        img.addEventListener('load', markLoaded);
        setTimeout(markLoaded, 1200);
      } else markLoaded();
    }

    // Image pools per panel: primary + optional alternate
    this.pools = Array.from(this.panelsEl.children).map((panel) =>
      Array.from(panel.querySelectorAll('img'))
    );

    this.AUTO_MS = 4300;
    this.TRANS_MS = 1500;
    this.fallbackTimer = null;

    if (this.rm || this.dataset.ripple === 'false') return;
    this.initGL();
  }

  disconnectedCallback() {
    this.running = false;
    this.timers.forEach(clearInterval);
    this.timers = [];
    if (this.fallbackTimer) clearInterval(this.fallbackTimer);
    if (this.onResize) window.removeEventListener('resize', this.onResize);
  }

  /* DOM crossfade slideshow — fallback when WebGL is unavailable */
  startFallback() {
    if (this.rm || this.fallbackTimer) return;
    const pools = this.pools;
    const slots = Array.from(this.panelsEl.children).map((panel, i) => {
      const base = panel.querySelector('img');
      const top = document.createElement('img');
      top.setAttribute('aria-hidden', 'true');
      top.style.cssText =
        'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;object-position:center top;opacity:0;transition:opacity 1.2s ease;';
      panel.insertBefore(top, base ? base.nextSibling : null);
      return { base, top, firstSrc: base ? base.src : '', idx: 0, pool: pools[i] || [] };
    });
    let turn = 0;
    this.fallbackTimer = setInterval(() => {
      if (document.hidden) return;
      const s = slots[turn % slots.length];
      turn++;
      if (!s || s.pool.length < 2) return;
      s.idx = (s.idx + 1) % s.pool.length;
      let src;
      if (s.idx === 0) src = s.firstSrc;
      else {
        const nxt = s.pool[s.idx];
        if (!nxt.complete || !nxt.naturalWidth) {
          s.idx = (s.idx - 1 + s.pool.length) % s.pool.length;
          return;
        }
        src = nxt.currentSrc || nxt.src;
      }
      s.top.src = src;
      s.top.style.opacity = '1';
      setTimeout(() => {
        s.base.src = s.top.src;
        s.top.style.opacity = '0';
      }, 1250);
    }, this.AUTO_MS);
  }

  initGL() {
    const canvas = this.canvas;
    if (!canvas) {
      this.startFallback();
      return;
    }
    let gl = null;
    try {
      gl =
        canvas.getContext('webgl', { alpha: false, antialias: false }) ||
        canvas.getContext('experimental-webgl');
    } catch (e) {
      gl = null;
    }
    if (!gl) {
      this.startFallback();
      return;
    }
    this.gl = gl;

    const VS =
      'attribute vec2 aPos; varying vec2 vUv; void main(){ vUv = aPos * .5 + .5; vUv.y = 1. - vUv.y; gl_Position = vec4(aPos, 0., 1.); }';
    const FS =
      'precision mediump float; varying vec2 vUv;' +
      'uniform sampler2D uSim;' +
      'uniform sampler2D uA0; uniform sampler2D uA1; uniform sampler2D uA2;' +
      'uniform sampler2D uB0; uniform sampler2D uB1; uniform sampler2D uB2;' +
      'uniform vec2 uSimTexel; uniform float uPanels; uniform vec3 uProg;' +
      'uniform vec2 uSA0; uniform vec2 uOA0; uniform vec2 uSB0; uniform vec2 uOB0;' +
      'uniform vec2 uSA1; uniform vec2 uOA1; uniform vec2 uSB1; uniform vec2 uOB1;' +
      'uniform vec2 uSA2; uniform vec2 uOA2; uniform vec2 uSB2; uniform vec2 uOB2;' +
      'void main(){' +
      '  float hl = texture2D(uSim, vUv - vec2(uSimTexel.x, 0.)).r;' +
      '  float hr = texture2D(uSim, vUv + vec2(uSimTexel.x, 0.)).r;' +
      '  float ht = texture2D(uSim, vUv - vec2(0., uSimTexel.y)).r;' +
      '  float hb = texture2D(uSim, vUv + vec2(0., uSimTexel.y)).r;' +
      '  vec2 grad = vec2(hr - hl, hb - ht);' +
      '  float hc = texture2D(uSim, vUv).r - .5;' +
      '  float p = min(floor(vUv.x * uPanels), uPanels - 1.);' +
      '  vec2 local = vec2(fract(vUv.x * uPanels), vUv.y);' +
      '  float pr = p < .5 ? uProg.x : (p < 1.5 ? uProg.y : uProg.z);' +
      '  float wave = sin(pr * 3.14159);' +
      '  vec2 da = local + grad * (0.22 + pr * 1.35);' +
      '  vec2 db = local + grad * (0.22 + (1. - pr) * 1.35);' +
      '  vec4 ca; vec4 cb;' +
      '  if (p < .5){ ca = texture2D(uA0, da * uSA0 + uOA0); cb = texture2D(uB0, db * uSB0 + uOB0); }' +
      '  else if (p < 1.5){ ca = texture2D(uA1, da * uSA1 + uOA1); cb = texture2D(uB1, db * uSB1 + uOB1); }' +
      '  else { ca = texture2D(uA2, da * uSA2 + uOA2); cb = texture2D(uB2, db * uSB2 + uOB2); }' +
      '  float m = clamp(smoothstep(.08, .92, pr + hc * 1.8 * wave), 0., 1.);' +
      '  vec4 col = mix(ca, cb, m);' +
      '  col.rgb += (grad.x + grad.y) * 1.6;' +
      '  col.rgb *= mix(.76, 1., smoothstep(0., .30, vUv.y));' +
      '  col.rgb *= mix(1., .42, smoothstep(.52, 1., vUv.y));' +
      '  gl_FragColor = vec4(col.rgb, 1.);' +
      '}';

    const shader = (type, src) => {
      const s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
      return s;
    };
    let prog;
    try {
      prog = gl.createProgram();
      gl.attachShader(prog, shader(gl.VERTEX_SHADER, VS));
      gl.attachShader(prog, shader(gl.FRAGMENT_SHADER, FS));
      gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error('link');
    } catch (e) {
      this.startFallback();
      return;
    }
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(prog, 'aPos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    const U = (this.U = {});
    [
      'uSim', 'uPanels', 'uSimTexel', 'uProg',
      'uA0', 'uA1', 'uA2', 'uB0', 'uB1', 'uB2',
      'uSA0', 'uOA0', 'uSB0', 'uOB0',
      'uSA1', 'uOA1', 'uSB1', 'uOB1',
      'uSA2', 'uOA2', 'uSB2', 'uOB2',
    ].forEach((n) => (U[n] = gl.getUniformLocation(prog, n)));

    gl.uniform1i(U.uSim, 0);
    gl.uniform1i(U.uA0, 1);
    gl.uniform1i(U.uA1, 2);
    gl.uniform1i(U.uA2, 3);
    gl.uniform1i(U.uB0, 4);
    gl.uniform1i(U.uB1, 5);
    gl.uniform1i(U.uB2, 6);

    const makeTex = () => {
      const t = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, t);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      return t;
    };

    const MAXT = gl.getParameter(gl.MAX_TEXTURE_SIZE) || 2048;
    const srcFor = (img) => {
      const w = img.naturalWidth,
        h = img.naturalHeight;
      const s = Math.min(1, MAXT / Math.max(w, h));
      if (s >= 1) return img;
      const c = document.createElement('canvas');
      c.width = Math.max(1, Math.round(w * s));
      c.height = Math.max(1, Math.round(h * s));
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      return c;
    };
    const texCache = [];
    const texFor = (img) => {
      for (let i = 0; i < texCache.length; i++) if (texCache[i].img === img) return texCache[i].tex;
      const t = makeTex();
      gl.bindTexture(gl.TEXTURE_2D, t);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, srcFor(img));
      texCache.push({ img, tex: t });
      return t;
    };

    /* water sim on CPU grid, uploaded as LUMINANCE texture */
    const GW = 200,
      GH = 120;
    let cur = new Float32Array(GW * GH);
    let prev = new Float32Array(GW * GH);
    const pix = new Uint8Array(GW * GH);
    const simTex = makeTex();
    gl.bindTexture(gl.TEXTURE_2D, simTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE, GW, GH, 0, gl.LUMINANCE, gl.UNSIGNED_BYTE, null);

    let panels = 3,
      solo = false,
      slots = [];
    const pools = this.pools;
    const hero = this;

    const coverTo = (img, sName, oName) => {
      const panelW = canvas.width / panels,
        panelH = canvas.height;
      const texA = img.naturalWidth / img.naturalHeight;
      const panA = panelW / panelH;
      let sx = 1,
        sy = 1;
      if (texA > panA) sx = panA / texA;
      else sy = texA / panA;
      gl.uniform2f(U[sName], sx, sy);
      gl.uniform2f(U[oName], (1 - sx) / 2, 0);
    };

    const bindSlots = () => {
      for (let i = 0; i < 3; i++) {
        const s = slots[Math.min(i, slots.length - 1)];
        const a = s.a,
          b = s.b || s.a;
        gl.activeTexture(gl.TEXTURE1 + i);
        gl.bindTexture(gl.TEXTURE_2D, texFor(a));
        gl.activeTexture(gl.TEXTURE4 + i);
        gl.bindTexture(gl.TEXTURE_2D, texFor(b));
        coverTo(a, 'uSA' + i, 'uOA' + i);
        coverTo(b, 'uSB' + i, 'uOB' + i);
      }
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, simTex);
    };

    const rebuildSlots = () => {
      const sets = solo ? [pools[1] || pools[0]] : pools;
      slots = sets.map((pool) => ({ pool, idx: 0, a: pool[0], b: null, t0: 0 }));
      gl.uniform3f(U.uProg, 0, 0, 0);
      bindSlots();
    };

    const resize = () => {
      const w = hero.clientWidth,
        h = hero.clientHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 1.75);
      const cw = Math.round(w * dpr),
        ch = Math.round(h * dpr);
      const nsolo = window.innerWidth <= 820;
      if (cw === canvas.width && ch === canvas.height && nsolo === solo && slots.length) return;
      canvas.width = cw;
      canvas.height = ch;
      gl.viewport(0, 0, canvas.width, canvas.height);
      solo = nsolo;
      panels = solo ? 1 : 3;
      gl.uniform1f(U.uPanels, panels);
      gl.uniform2f(U.uSimTexel, 1 / GW, 1 / GH);
      rebuildSlots();
    };
    this.onResize = resize;

    const splat = (nx, ny, force) => {
      const gx = Math.round(nx * (GW - 1)),
        gy = Math.round(ny * (GH - 1));
      for (let dy = -2; dy <= 2; dy++) {
        for (let dx = -2; dx <= 2; dx++) {
          const x = gx + dx,
            y = gy + dy;
          if (x < 1 || y < 1 || x >= GW - 1 || y >= GH - 1) continue;
          const fall = 1 - (Math.abs(dx) + Math.abs(dy)) * 0.22;
          if (fall > 0) prev[y * GW + x] += force * fall;
        }
      }
    };

    let lastMX = -1,
      lastMY = -1;
    hero.addEventListener(
      'pointermove',
      (e) => {
        const r = hero.getBoundingClientRect();
        const nx = (e.clientX - r.left) / r.width,
          ny = (e.clientY - r.top) / r.height;
        if (lastMX >= 0) {
          const d = Math.abs(nx - lastMX) + Math.abs(ny - lastMY);
          splat(nx, ny, Math.min(2.2, 14 * d + 0.25));
        }
        lastMX = nx;
        lastMY = ny;
      },
      { passive: true }
    );
    hero.addEventListener('pointerleave', () => (lastMX = -1));
    hero.addEventListener(
      'pointerdown',
      (e) => {
        const r = hero.getBoundingClientRect();
        splat((e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height, 4.5);
      },
      { passive: true }
    );

    this.running = true;
    let visible = true;
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((en) => (visible = en[0].isIntersecting)).observe(hero);
    }

    this.timers.push(
      setInterval(() => {
        if (!this.running || !visible || document.hidden) return;
        splat(0.15 + Math.random() * 0.7, 0.2 + Math.random() * 0.6, 1.6);
      }, 2600)
    );

    /* auto liquid transitions — the water sim drives the image morph */
    let turn = 0;
    const startTransition = () => {
      if (!this.running || !visible || document.hidden || !slots.length) return;
      const i = turn % slots.length;
      turn++;
      const s = slots[i];
      if (s.b || s.pool.length < 2) return;
      s.idx = (s.idx + 1) % s.pool.length;
      const nxt = s.pool[s.idx];
      if (!nxt.complete || !nxt.naturalWidth) {
        s.idx = (s.idx - 1 + s.pool.length) % s.pool.length;
        return;
      }
      s.b = nxt;
      s.t0 = performance.now();
      bindSlots();
      const n = slots.length;
      const cx = (i + 0.5) / n;
      for (let k = 0; k < 6; k++) {
        splat(cx + (Math.random() - 0.5) * (0.85 / n), 0.12 + Math.random() * 0.75, 2.4);
      }
    };
    this.timers.push(setInterval(startTransition, this.AUTO_MS));

    const step = () => {
      for (let y = 1; y < GH - 1; y++) {
        const row = y * GW;
        for (let x = 1; x < GW - 1; x++) {
          const i = row + x;
          const v = (cur[i - 1] + cur[i + 1] + cur[i - GW] + cur[i + GW]) / 2 - prev[i];
          prev[i] = v * 0.976;
        }
      }
      const tmp = cur;
      cur = prev;
      prev = tmp;
      for (let j = 0; j < cur.length; j++) {
        const h = cur[j] * 46 + 128;
        pix[j] = h < 0 ? 0 : h > 255 ? 255 : h;
      }
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, simTex);
      gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, GW, GH, gl.LUMINANCE, gl.UNSIGNED_BYTE, pix);
    };

    const progs = [0, 0, 0];
    const frame = (now) => {
      if (visible) {
        step();
        let dirty = false;
        for (let i = 0; i < slots.length; i++) {
          const s = slots[i];
          if (s.b) {
            const t = Math.min(1, (now - s.t0) / this.TRANS_MS);
            const e = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
            progs[i] = e;
            if (t >= 1) {
              s.a = s.b;
              s.b = null;
              progs[i] = 0;
              dirty = true;
            }
          } else progs[i] = 0;
        }
        if (dirty) bindSlots();
        gl.uniform3f(U.uProg, progs[0], progs[1] || 0, progs[2] || 0);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
      }
      if (this.running) requestAnimationFrame(frame);
    };

    /* start once the primary panel images are decoded */
    const primaries = pools.map((p) => p[0]).filter(Boolean);
    let pending = primaries.length;
    const ready = () => {
      if (--pending > 0) return;
      try {
        resize();
        window.addEventListener('resize', resize);
        canvas.classList.add('is-on');
        this.panelsEl.classList.add('gl-on');
        requestAnimationFrame(frame);
      } catch (e) {
        this.running = false;
        this.timers.forEach(clearInterval);
        this.timers = [];
        canvas.classList.remove('is-on');
        this.panelsEl.classList.remove('gl-on');
        this.startFallback();
      }
    };
    primaries.forEach((im) => {
      if (im.complete && im.naturalWidth) ready();
      else if (im.complete) {
        this.running = false;
        this.startFallback();
      } else {
        im.addEventListener('load', ready);
        im.addEventListener('error', () => {
          this.running = false;
          this.startFallback();
        });
      }
    });
  }
}

if (!customElements.get('icekivi-hero')) {
  customElements.define('icekivi-hero', IcekiviHero);
}
